import { axios } from "../../../lib/axios";
import { schedulingDataChanged } from "../../../lib/schedulingCache";

export const SCHEDULING_PREFIX = "scheduling";
export const DEFAULT_LIMIT = 100;
export const MAX_LIMIT = 500;

export function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function formatIfMatch(version) {
  if (version === undefined || version === null || version === "") return undefined;
  const raw = String(version).replace(/^W\//, "").replace(/"/g, "");
  return `"${raw}"`;
}

export function versionBody(expectedVersion) {
  if (expectedVersion === undefined || expectedVersion === null || expectedVersion === "") {
    return {};
  }
  return { expectedVersion: Number(expectedVersion) };
}

export function ifMatchHeaders(version) {
  const value = formatIfMatch(version);
  return value ? { "If-Match": value } : {};
}

export function idempotencyHeaders(key = createIdempotencyKey()) {
  return { "Idempotency-Key": key };
}

export function compactParams(params) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    out[key] = value;
  });
  return out;
}

export function schedulingGet(path, params, { consistency, ...config } = {}) {
  return axios.get(`${SCHEDULING_PREFIX}/${path}`, {
    ...config,
    ...(consistency === "strong" ? { headers: { ...config.headers, "X-Scheduling-Consistency": "strong" } } : {}),
    params: compactParams(params),
  });
}

async function schedulingWrite(send) {
  try {
    const result = await send();
    schedulingDataChanged();
    return result;
  } catch (error) {
    // A transport failure or post-commit error can hide a successful change.
    // Reconcile cached queries once; never pin the browser to the writer.
    const response = error.response;
    if (!response || response.status >= 500 || response.headers?.["x-scheduling-consistency"]) {
      schedulingDataChanged();
    }
    throw error;
  }
}

export async function schedulingPost(path, body, config) {
  return schedulingWrite(() => axios.post(`${SCHEDULING_PREFIX}/${path}`, body, config));
}

export async function schedulingPut(path, body, config) {
  return schedulingWrite(() => axios.put(`${SCHEDULING_PREFIX}/${path}`, body, config));
}

export async function schedulingDelete(path, params, config) {
  return schedulingWrite(() => axios.delete(`${SCHEDULING_PREFIX}/${path}`, {
    ...config,
    params: compactParams(params),
  }));
}

export class IncompleteSchedulingDataError extends Error {
  constructor(message = "Too many results to load at once. Narrow the date range or choose a practitioner.") {
    super(message); this.name = "IncompleteSchedulingDataError";
  }
}
export async function fetchAllCursorPages({ fetchPage, itemsKey, maxPages = 20, signal }) {
  const all = [];
  const seen = new Set();
  let cursor;
  for (let page = 0; page < maxPages; page += 1) {
    if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
    const res = await fetchPage(cursor);
    if (!Array.isArray(res?.[itemsKey])) throw new IncompleteSchedulingDataError("Could not load complete scheduling data. Try again.");
    all.push(...res[itemsKey]);
    cursor = res.nextCursor;
    if (cursor == null || cursor === "") return all;
    if (typeof cursor !== "string" || seen.has(cursor)) throw new IncompleteSchedulingDataError("Could not continue loading scheduling data. Try again.");
    seen.add(cursor);
  }
  throw new IncompleteSchedulingDataError();
}

export function asNamedList(res, listKey, singleKey) {
  if (Array.isArray(res?.[listKey])) {
    return { items: res[listKey], nextCursor: res.nextCursor || null };
  }
  if (res?.[singleKey] && !Array.isArray(res[singleKey])) {
    return { items: [res[singleKey]], nextCursor: null };
  }
  return { items: [], nextCursor: res?.nextCursor || null };
}

export function asAvailabilityList(res) {
  if (Array.isArray(res?.availability)) {
    return { items: res.availability, nextCursor: res.nextCursor || null };
  }
  if (res?.availability && typeof res.availability === "object") {
    return { items: [res.availability], nextCursor: null };
  }
  return { items: [], nextCursor: null };
}
